"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/app/utils/className";

/** Fraction of the viewport height an element must reach before it reveals. */
const TRIGGER_RATIO = 0.92;

/**
 * How long to keep sweeping after the last scroll event. ScrollSmoother keeps
 * easing the content for about a second after native scrolling stops, so the
 * final resting position arrives well after the last scroll event fires.
 */
const SETTLE_MS = 1400;

type Pending = { element: HTMLElement; show: () => void };

/**
 * One rAF-throttled sweep for every pending reveal on the page.
 *
 * IntersectionObserver is the usual tool here, but it only reports threshold
 * *crossings*: a scroll that jumps an element from below the fold to above it
 * between two frames -- an anchor jump, an End keypress, a fast fling -- never
 * crosses, and the element stays invisible for good. A position sweep has no
 * such gap, and it drains: once every reveal on the page has fired, the loop
 * and its listeners are torn down.
 */
const pending = new Set<Pending>();
let frame = 0;
let sweepUntil = 0;
let listening = false;

const sweep = () => {
  const limit = window.innerHeight * TRIGGER_RATIO;

  pending.forEach((item) => {
    if (item.element.getBoundingClientRect().top >= limit) return;
    item.show();
    pending.delete(item);
  });

  if (pending.size === 0) {
    stop();
    return;
  }

  frame = performance.now() < sweepUntil ? requestAnimationFrame(sweep) : 0;
};

const wake = () => {
  sweepUntil = performance.now() + SETTLE_MS;
  if (frame || pending.size === 0) return;
  frame = requestAnimationFrame(sweep);
};

const start = () => {
  if (listening) return;
  listening = true;
  window.addEventListener("scroll", wake, { passive: true });
  window.addEventListener("resize", wake, { passive: true });
};

function stop() {
  if (frame) cancelAnimationFrame(frame);
  frame = 0;
  if (!listening) return;
  listening = false;
  window.removeEventListener("scroll", wake);
  window.removeEventListener("resize", wake);
}

const watch = (item: Pending) => {
  pending.add(item);
  start();
  wake();

  return () => {
    pending.delete(item);
    if (pending.size === 0) stop();
  };
};

interface RevealProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Stagger, in milliseconds, applied as a CSS transition delay. */
  delay?: number;
}

/**
 * Scroll-entry reveal.
 *
 * The design prototype used `animation-timeline: view()`, which reads the
 * element's position inside the scrollport. ScrollSmoother moves content with a
 * transform rather than scrolling it, so a view timeline never advances here.
 * Reduced motion is handled in CSS, which neutralises `.dc-reveal` outright.
 */
export const Reveal = ({
  children,
  className,
  delay = 0,
  style,
  ...props
}: RevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    return watch({ element, show: () => setShown(true) });
  }, []);

  return (
    <div
      ref={ref}
      data-shown={shown}
      className={cn("dc-reveal", className)}
      style={
        { "--dc-reveal-delay": `${delay}ms`, ...style } as React.CSSProperties
      }
      {...props}
    >
      {children}
    </div>
  );
};
